import { useContext, useEffect, useRef, useState } from 'react';
import {
  BoxSection,
  ButtonDefault,
  ButtonPrimary,
  TextL,
} from 'app/components';
import { ButtonsArea, SimulationStepContainer } from './styles';
import { ApplicationRequestContext } from '../ApplicationRequest';
import {
  CreditSimulator,
  CreditSimulatorRefs,
} from 'modules/products/pages/ApplicationRequest/SimulationV2Step/CreditSimulatorV2';
import { ArrowBack } from '@mui/icons-material';
import { useTheme } from '@mui/material';
import { ConsigneeOrganType, isCtOrganType } from '../ConsignedStep/interfaces';
import { notification } from 'antd';
import dayjs from 'dayjs';
import { IElegibility, IElegibilityConfigs } from './CreditSimulatorV2/types';
import { formatCurrency } from 'app/utils/normalizer';

type SimulationV2StepProps = {
  isCalcSimulation?: boolean;
};

const SimulationV2Step = ({ isCalcSimulation }: SimulationV2StepProps) => {
  /* Hooks */
  const [api, context] = notification.useNotification();
  const theme = useTheme();
  const formRef = useRef<CreditSimulatorRefs>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const {
    product,
    simulationV2,
    simulationV2Request,
    borrowerQualification,
    billetValidationResponse,
    qrcodeEmvValidationResponse,
    setSimulationV2,
    onBack,
    onForward,
    consignedProduct,
    tacValidationError,
    borrowerData,
    getElegibilityRules,
    elegibilityError,
  } = useContext(ApplicationRequestContext);

  /* States */
  const [tabIndexSelected, setTabIndexSelected] = useState('');
  const [isValidElegibility, setIsValidElegibility] = useState(true);
  const [isGuaranteeValid, setIsGuaranteeValid] = useState(true);
  const [hasFirstPaymentDateFromForm, setHasFirstPaymentDateFromForm] =
    useState(false);

  /* Variables */
  const canCalculateCT =
    !isCtOrganType(product?.organ_type) ||
    !!simulationV2Request?.firstPaymentDate ||
    hasFirstPaymentDateFromForm;
  const hasTimeValidation: boolean =
    product?.allow_disbursement_in_window || false;
  const isFgts = product?.organ_type === ConsigneeOrganType.FGTS;
  const isSiape =
    product?.organ_type === ConsigneeOrganType.SIAPE &&
    !!consignedProduct?.available_balance;
  const consignedAmount = consignedProduct?.available_balance || 0;
  const consignedParcels = 96;
  const consignedTotalAmount = consignedAmount * consignedParcels;

  const isAmountAllowed =
    tabIndexSelected === '0'
      ? (consignedProduct?.available_balance || 0) >
        (simulationV2?.payment_amount || 0)
      : consignedTotalAmount >= (simulationV2?.requested_amount || 0);

  const isButtonDisabled =
    (isSiape && !isAmountAllowed) ||
    (!formRef.current?.isValid && !!simulationV2?.requested_amount) ||
    (isCtOrganType(product?.organ_type) && !canCalculateCT) ||
    !!tacValidationError ||
    (!!simulationV2?.financed_amount && !isGuaranteeValid);

  /* Handlers */
  const handleSimulator = () => {
    formRef.current?.simulate();
  };

  const handleRecalculate = () => {
    api.destroy();
    formRef.current?.destroyNotifications();
    formRef.current?.simulate();
  };

  const handleContinue = async () => {
    const isGuaranteesValid = await formRef.current?.validateGuarantees();

    if (!isGuaranteesValid || !simulationV2?.financed_amount) {
      return;
    }

    onForward();
  };

  const handleBack = () => {
    formRef.current?.reset();
    setIsGuaranteeValid(true);
    setSimulationV2(null, null, '0');
    onBack();
  };

  const getElegibilityRulesHandler = async (id: string) => {
    const data: IElegibility | null = await getElegibilityRules(id);

    if (data?.configs) {
      validateElegibility(data.configs);
    }
  };

  const validateElegibility = (configs: IElegibilityConfigs) => {
    if (configs) {
      const age = dayjs().diff(borrowerData?.birth_date, 'year');
      const education = borrowerData?.education_level;
      const installments = simulationV2?.num_periods;
      const operationValue = simulationV2?.total_amount_owed;
      const interestRate = simulationV2?.interest_rate;

      if (
        configs?.age?.min &&
        configs?.age?.max &&
        (age < configs?.age?.min || age > configs?.age?.max)
      ) {
        setIsValidElegibility(false);
        api.error({
          message: 'Idade inválida',
          description: `A idade deve estar entre ${configs?.age?.min} e ${configs?.age?.max} anos.`,
          duration: 10000,
          pauseOnHover: true,
        });
      }

      if (
        !!education &&
        !configs?.education?.allowedDegrees.includes(education)
      ) {
        setIsValidElegibility(false);
        api.error({
          message: 'Grau de instrução inválido',
          duration: 10000,
          description:
            'O grau de escolaridade informado não permite a contratação deste produto',
        });
      }

      if (
        installments &&
        configs?.installments?.min &&
        configs?.installments?.max &&
        (installments < configs?.installments.min ||
          installments > configs?.installments.max)
      ) {
        setIsValidElegibility(false);
        api.error({
          message: 'Parcelas inválidas',
          duration: 10000,
          description: `O número de parcelas deve estar entre ${configs?.installments.min} e ${configs?.installments.max}.`,
        });
      }

      if (
        interestRate &&
        configs?.interestRate?.min &&
        configs?.interestRate?.max &&
        (interestRate < configs?.interestRate.min ||
          interestRate > configs?.interestRate.max)
      ) {
        setIsValidElegibility(false);
        api.error({
          message: 'Taxa de juros inválida',
          duration: 10000,
          description: `A taxa de juros deve estar entre ${configs?.interestRate.min} e ${configs?.interestRate.max}.`,
        });
      }

      if (
        operationValue &&
        configs?.operationValue?.min &&
        configs?.operationValue?.max &&
        (operationValue < configs?.operationValue.min ||
          operationValue > configs?.operationValue.max)
      ) {
        setIsValidElegibility(false);
        api.error({
          message: 'Valor de operação inválido',
          duration: 10000,
          description: `O valor da operação deve estar entre ${formatCurrency(
            configs?.operationValue.min,
          )} e ${formatCurrency(configs?.operationValue.max)}.`,
        });
      }
    }
  };

  useEffect(() => {
    if (product?.id) {
      getElegibilityRulesHandler(product?.id);
    }
  }, [product?.id]);

  useEffect(() => {
    if (!simulationV2?.financed_amount) {
      setIsGuaranteeValid(true);
    }
  }, [simulationV2?.financed_amount]);

  useEffect(() => {
    if (elegibilityError?.message) {
      api.error({
        message: elegibilityError.message,
        description: elegibilityError?.description || '',
        duration: 10000,
        pauseOnHover: true,
      });
    }
  }, [elegibilityError]);

  return (
    <BoxSection>
      {context}
      <SimulationStepContainer ref={containerRef}>
        <CreditSimulator
          ref={formRef}
          product={product}
          simulationResponse={simulationV2}
          simulationRequest={simulationV2Request}
          billetValidationResponse={billetValidationResponse}
          qualification={borrowerQualification}
          qrcodeEmvValidationResponse={qrcodeEmvValidationResponse}
          hasTimeValidation={hasTimeValidation}
          onCTFirstPaymentDateChange={setHasFirstPaymentDateFromForm}
          onGuaranteeValidChange={setIsGuaranteeValid}
          onSimulate={(simulation, simulationRequest, tabIndex) => {
            setTabIndexSelected(tabIndex);
            setSimulationV2(simulation, simulationRequest, tabIndex);
            setTimeout(() =>
              containerRef.current?.scrollTo({
                top: 9999,
                behavior: 'smooth',
              }),
            );
          }}
        />
      </SimulationStepContainer>
      <ButtonsArea>
        <div>
          {!isFgts && (
            <ButtonDefault
              style={{
                maxWidth: '200px',
              }}
              onClick={() => formRef.current?.reset()}
            >
              <TextL style={{ color: theme.palette.brand.primary.base }}>
                Limpar condições
              </TextL>
            </ButtonDefault>
          )}
        </div>
        <div className="btn-preview">
          {!isCalcSimulation && (
            <ButtonDefault onClick={handleBack}>
              <ArrowBack />
            </ButtonDefault>
          )}
          {simulationV2 && simulationV2.requested_amount && (
            <>
              <ButtonDefault onClick={() => formRef.current?.copyPreview()}>
                <TextL
                  style={{
                    color: theme.palette.brand.primary.base,
                    minWidth: '200px',
                  }}
                >
                  Copiar resultados
                </TextL>
              </ButtonDefault>
              {!isCalcSimulation && (
                <ButtonDefault
                  style={{ color: theme.palette.brand.primary.base }}
                  onClick={handleRecalculate}
                >
                  <TextL style={{ color: theme.palette.brand.primary.base }}>
                    Recalcular
                  </TextL>
                </ButtonDefault>
              )}
            </>
          )}
          {isCalcSimulation ? (
            <ButtonPrimary
              disabled={isButtonDisabled || !isValidElegibility}
              onClick={
                simulationV2?.requested_amount
                  ? handleRecalculate
                  : handleSimulator
              }
            >
              <TextL style={{ color: theme.palette.common.white }}>
                {!simulationV2?.requested_amount ? 'Calcular' : 'Recalcular'}
              </TextL>
            </ButtonPrimary>
          ) : (
            <ButtonPrimary
              disabled={isButtonDisabled || !isValidElegibility}
              onClick={
                !simulationV2?.requested_amount
                  ? handleSimulator
                  : handleContinue
              }
            >
              <TextL style={{ color: theme.palette.common.white }}>
                {!simulationV2?.requested_amount ? 'Calcular' : 'Avançar'}
              </TextL>
            </ButtonPrimary>
          )}
        </div>
      </ButtonsArea>
    </BoxSection>
  );
};

export default SimulationV2Step;
